/**
 * Equity Card Service
 *
 * Service layer for equity card rendering, handling data gathering
 * and template resolution.
 *
 * Usage:
 *   import { EquityCardService } from './services/EquityCardService.js';
 *
 *   const cardLines = EquityCardService.renderCardLines(key, userShares, clientData, bases, values, interest);
 *   // cardLines.holdings, cardLines.costbasis, cardLines.uncalled, cardLines.trade, cardLines.subtitle
 */

import { EquityCardFormatter } from '../formatters/EquityCardFormatter.js';

const EquityCardService = {
	/**
	 * Render all template lines for an equity card
	 *
	 * @param {string} key - Share class key (e.g., "C", "A", "ESOP")
	 * @param {Object} userShares - User's share data for this class (uD[key])
	 * @param {Object} clientData - Full client data object
	 * @param {number} bases - Cost basis value for this class
	 * @param {number} values - Current value for this class
	 * @param {number} interest - Accrued interest for this class (for ACC clients)
	 * @param {Object} calculatorResult - Full calculator result (window.lastCalculationResult)
	 * @param {Object} [options] - Additional options
	 * @param {string} [options.stockStatus] - User's stock status for eligibility calculation (e.g., "10", "100", "Associate")
	 * @param {Object} [options.userData] - Full user data object (for configured variables)
	 * @param {Object} [options.aggregates] - Cap table aggregates for this class (totalOwned, shareholderCount, etc.)
	 * @param {string} [options.client] - Client code (e.g., "portblakely") for path-based variable resolution
	 * @returns {Object} - Rendered lines and variable map
	 *   {
	 *     line1: string,  // Primary line (e.g., "You Own: 1,000 shares")
	 *     line2: string,  // Secondary line (e.g., "Cost Basis: $10,000")
	 *     line3: string,  // Tertiary line
	 *     line4: string,  // Additional line
	 *     vars: Object    // Variable map for conditional rendering decisions
	 *   }
	 */
	renderCardLines: function (key, userShares, clientData, bases, values, interest, calculatorResult, options = {}) {
		const equityConfig = clientData.equity?.[key] || {};

		// Find per-class calculator data from breakdown
		const calculatorData = calculatorResult?.breakdown?.find((c) => c.classKey === key) || null;

		// Build variable map from user, client, and calculator data
		const vars = EquityCardFormatter.buildVariableMap({
			userShares: userShares || {},
			equityConfig,
			bases: bases || 0,
			values: values || 0,
			interest: interest || 0,
			calculatorData,
			stockStatus: options.stockStatus || null,
			userData: options.userData || null,
			clientData: clientData,
			aggregates: options.aggregates || null,
			classKey: key,
			client: options.client || null,
		});

		return {
			line1: EquityCardFormatter.renderLine('line1', equityConfig, vars),
			line2: EquityCardFormatter.renderLine('line2', equityConfig, vars),
			line3: EquityCardFormatter.renderLine('line3', equityConfig, vars),
			line4: EquityCardFormatter.renderLine('line4', equityConfig, vars),
			vars, // Return vars for conditional rendering decisions in the caller
		};
	},

	/**
	 * Get the default template for a given line
	 * Useful for displaying defaults in admin UI
	 *
	 * @param {string} lineKey - Line key ('line1', 'line2', 'line3', 'line4')
	 * @returns {string} - Default template string
	 */
	getDefaultTemplate: function (lineKey) {
		return EquityCardFormatter.defaultTemplates[lineKey] || '';
	},
};

export { EquityCardService };
